"use client";

import { createContext, useContext, useState } from "react";
import type { useRangeEditor } from "@/hooks/useRangeEditor";

type EditorBundle = ReturnType<typeof useRangeEditor>;
type SetEditor = (b: EditorBundle | null) => void;

// Two contexts so the publisher (EditorShell) can subscribe to the *setter*
// without re-rendering whenever the bundle changes, while subscribers
// (modals like MergeFlowModal / ExportModal) only see the bundle.
const EditorCtx = createContext<EditorBundle | null>(null);
const SetEditorCtx = createContext<SetEditor>(() => {});

/**
 * Top-level publisher for the active editor bundle. Mount once inside AppShell
 * so the workspace and modals see the same value. The bundle is set by
 * EditorShell on each state-change and cleared on unmount.
 */
export function EditorBusProvider({ children }: { children: React.ReactNode }) {
  const [editor, setEditor] = useState<EditorBundle | null>(null);
  return (
    <SetEditorCtx.Provider value={setEditor}>
      <EditorCtx.Provider value={editor}>{children}</EditorCtx.Provider>
    </SetEditorCtx.Provider>
  );
}

/** Returns the active editor bundle, or null if no range is open. */
export function useEditor(): EditorBundle | null {
  return useContext(EditorCtx);
}

/** Stable setter for publishers. Returned reference is identity-stable. */
export function useSetEditor(): SetEditor {
  return useContext(SetEditorCtx);
}
